import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Brand } from "./Brand";
import { ContactButton } from "./ContactButton";
import { NAV_LINKS } from "@/lib/content";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const isActive = (link) => link.id === "coaching"
    ? pathname === "/" && hash === "#coaching"
    : pathname === link.to && (link.id !== "home" || hash !== "#coaching");

  useEffect(() => { setOpen(false); }, [pathname, hash]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#e4dacb]/80 bg-[#faf5ee]/85 backdrop-blur-xl" data-testid="site-nav">
      <div className="mx-auto flex h-[88px] max-w-7xl items-center justify-between gap-4 px-5 sm:h-24 sm:px-8 lg:px-12">
        <Brand onClick={() => setOpen(false)} />
        <nav aria-label="Main navigation" className="hidden items-center gap-5 lg:flex xl:gap-8">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} aria-current={isActive(l) ? (l.id === "coaching" ? "location" : "page") : undefined}
              className={`relative whitespace-nowrap rounded-sm py-2 text-sm font-medium transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#7a5615] xl:text-base ${isActive(l) ? "text-[#221e1b]" : "text-[#5d544c] hover:text-[#221e1b]"}`} data-testid={`nav-link-${l.id}`}>
              {l.label}
              {isActive(l) && <motion.span layoutId="nav-dot" className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#a8792a]" />}
            </Link>
          ))}
          <ContactButton testId="nav-contact-btn" />
        </nav>
        <button
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#e4dacb] lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            id="mobile-navigation"
            className="overflow-hidden border-t border-[#e4dacb] bg-[#faf5ee] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.id}`}
                  aria-current={isActive(l) ? (l.id === "coaching" ? "location" : "page") : undefined}
                  className={`font-display rounded-sm py-2 text-3xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#7a5615] ${isActive(l) ? "text-[#a8792a]" : "text-[#221e1b]"}`}>
                  {l.label}
                </Link>
              ))}
              <ContactButton testId="mobile-nav-contact-btn" className="mt-4 self-start" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
